import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/glTransaction.mutation"

import { CancelGlTransactionDocument, type GlTransactionCancelInput } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseCancelGlTransactionReturn {
  cancelGlTransaction: (input: GlTransactionCancelInput) => Promise<boolean>
  isCancelling: boolean
}

export const useCancelGlTransaction = (): UseCancelGlTransactionReturn => {
  const [{ fetching }, executeMutation] = useMutation(CancelGlTransactionDocument)
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const cancelGlTransaction = useCallback(
    async (input: GlTransactionCancelInput): Promise<boolean> => {
      const result = await executeMutation({ input })

      if (result.error) {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: result.error.message || "Failed to cancel transaction. Please try again.",
        })
        return false
      }

      const output = result.data?.glTransaction.cancel

      if (output?.__typename === "GlTransactionFailure") {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: output.error.message || "Failed to cancel transaction. Please try again.",
        })
        return false
      }

      const referenceNumber =
        output?.__typename === "GlTransactionCancelSuccess" ? output.glTransaction.referenceNumber : undefined

      notify({
        type: NotificationType.Success,
        silent: false,
        message: `GL Entry cancelled.${referenceNumber ? ` Reference: ${referenceNumber}` : ""}`,
      })
      return true
    },
    [executeMutation, notify]
  )

  return { cancelGlTransaction, isCancelling: fetching }
}
